import { AnchorButton, Button, Dialog, Icon, Intent, Tooltip } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router'
import Client from '../../../client'
import { UserPermission } from '../../../current/types'
import { AppState } from '../../../store'
import { ListingStrategyRun } from '../../../types/api'
import GridSquare from '../GridSquare'
import ListingStrategyChangeDialog from './ListingStrategyChangeDialog'

interface Props {
  listingStrategyRun: ListingStrategyRun
  listingStrategyId: number
  productId: number
  onChange: Function
  permissions: UserPermission[]
  overridesActive?: boolean
}

interface State {
  run: ListingStrategyRun
  changeStrategyDialogOpen: boolean
  loading: boolean
}

class ListingStrategyRunDetails extends React.Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      run: props.listingStrategyRun,
      changeStrategyDialogOpen: false,
      loading: false
    }
  }

  endRun = async () => {
    this.setState({ loading: true })
    const { run } = this.state
    run.running = false
    Client.updateListingStrategyRun(run.id, { listingStrategyRun: run }).then(
      res => {
        this.setState(s => ({
          run: {
            ...s.run,
            running: res.data.listingStrategyRun.running
          },
          loading: false
        }))
      },
      () => {
        this.setState({ loading: false })
      }
    )
  }

  restartRun = async () => {
    this.setState({ loading: true })
    // eslint-disable-next-line prefer-destructuring
    const run: ListingStrategyRun & { listingStrategyStepId?: number } = this.state.run
    run.running = true
    run.listingStrategyStepId = null
    Client.updateListingStrategyRun(run.id, { listingStrategyRun: run }).then(
      res => {
        const { listingStrategyRun } = res.data
        const { currentStep } = listingStrategyRun
        this.setState(s => ({
          run: {
            ...s.run,
            currentStep,
            listingStrategyStepId: listingStrategyRun.listingStrategyStepId,
            running: listingStrategyRun.running
          },
          loading: false
        }))
      },
      () => {
        this.setState({ loading: false })
      }
    )
  }

  currentStep = () => {
    if (this.state.loading) return null
    const { run } = this.state
    if (!run.running) return null
    if (!run.currentStep) return 'Waiting To Start'
    return run.currentStep.rank + 1
  }

  render() {
    const { listingStrategyId, productId } = this.props
    const { run, loading } = this.state
    return (
      <div className="listing-strategy-run-details">
        <Dialog
          canEscapeKeyClose
          canOutsideClickClose
          isOpen={this.state.changeStrategyDialogOpen}
          title="Change Listing Strategy"
          onClose={() => {
            this.setState({
              changeStrategyDialogOpen: false
            })
          }}
        >
          <ListingStrategyChangeDialog
            listingStrategyId={listingStrategyId}
            productId={productId}
            onClose={() => {
              this.setState({
                changeStrategyDialogOpen: false
              })
            }}
            onChange={this.props.onChange}
          />
        </Dialog>

        <div className="drawer-header">
          <Icon icon={IconNames.REFRESH} /> Listing Strategy Details
          <AnchorButton
            icon="help"
            href="https://help.uprightlabs.com/en-us/lister/guide-to-settings-listing-strategies"
            target="_blank"
            rel="noopener noreferrer"
            className="help-icon"
            minimal
          />
        </div>
        <div className="details">
          <GridSquare
            title="Name"
            contents={
              <>
                {this.props.permissions.includes('settings.strategies') && (
                  <Link to={`/admin/listing_strategies/${listingStrategyId}/edit`}>{run.name}</Link>
                )}
                {!this.props.permissions.includes('settings.strategies') && run.name}
                {!run.running && (
                  <Tooltip>
                    <Button
                      tabIndex={0}
                      icon={IconNames.SWAP_HORIZONTAL}
                      style={{ cursor: 'pointer', marginLeft: 15 }}
                      onClick={() => this.setState({ changeStrategyDialogOpen: true })}
                      disabled={!!this.props.overridesActive}
                    />
                    {this.props.overridesActive ? (
                      <span>Cannot change strategy while a price override is active, remove the override first</span>
                    ) : (
                      <div>Change listing strategy</div>
                    )}
                  </Tooltip>
                )}
              </>
            }
          />
          <GridSquare title="Current Step" contents={this.currentStep()} />
          <GridSquare title="Total Steps" contents={run.totalSteps} />
          <GridSquare title="Running?" contents={run.running ? 'Yes' : 'No'} />
          <GridSquare
            title="Actions"
            contents={
              run.running ? (
                <Tooltip>
                  <Button
                    loading={loading}
                    icon={IconNames.REMOVE}
                    intent={Intent.DANGER}
                    onClick={this.endRun}
                    disabled={this.props.overridesActive}
                  >
                    End Run
                  </Button>
                  {this.props.overridesActive ? (
                    <span>Cannot end strategy run while a price override is active, remove the override first</span>
                  ) : (
                    <span>
                      This will end the listing strategy run, which means the listing will no longer be automatically
                      relisted.
                      <br />
                      This will not end the item early
                    </span>
                  )}
                </Tooltip>
              ) : (
                <Tooltip>
                  <Button
                    loading={loading}
                    icon={IconNames.REDO}
                    intent={Intent.PRIMARY}
                    onClick={this.restartRun}
                    disabled={this.props.overridesActive}
                  >
                    Restart Run
                  </Button>
                  {this.props.overridesActive ? (
                    <span>Cannot restart strategy run while a price override is active, remove the override first</span>
                  ) : (
                    <span>
                      This will start the listing strategy run over at the first step.
                      <br />
                      Be advised: this listing will be relisted.
                    </span>
                  )}
                </Tooltip>
              )
            }
          />
        </div>
      </div>
    )
  }
}

export default connect((s: AppState) => ({
  permissions: s.current.user.permissions
}))(ListingStrategyRunDetails)
